import { useEffect, useState } from "react";
import {
  API_ROLE,
  MENU_ALL,
  MENU_ASSIGNMENT,
  MENU_FIND,
} from "../../../../config/Endpoints.js";
import { Button, Checkbox, Select, Spinner } from "../../../components/ui/index.ts";
import { EmptyStateMessage } from "../../../components/shared/empty-state-message/empty-state-message.tsx";
import { ApiError, GENERIC_ERROR, postApi } from "../../../utils/api.ts";
import { accessToken } from "../../../utils/auth.ts";
import { showError, showSuccess } from "../../../utils/toast.ts";
import { useRoleManagementQuery } from "../use-role-management-query.ts";
import { roleLabel, type ListResponse, type MenuItem, type Role } from "../types.ts";
import styles from "../role-management.module.css";

export function MenuAssignmentTab() {
  const [roleId, setRoleId] = useState("");
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const roles = useRoleManagementQuery<ListResponse<Role>>(API_ROLE);
  const allMenus = useRoleManagementQuery<ListResponse<MenuItem>>(MENU_ALL);
  const assigned = useRoleManagementQuery<ListResponse<MenuItem>>(
    roleId ? `${MENU_FIND}/${encodeURIComponent(roleId)}` : null,
    refreshKey,
  );
  const menus = assigned.data?.data ?? allMenus.data?.data ?? [];
  const loading = assigned.loading || allMenus.loading;
  const error = roles.error || assigned.error || allMenus.error;

  useEffect(() => {
    const next: Record<number, boolean> = {};
    for (const menu of assigned.data?.data ?? []) {
      next[menu.id] = Boolean(menu.assigned);
      for (const child of menu.subMenu ?? []) {
        next[child.id] = Boolean(menu.assigned && child.assigned);
      }
    }
    setChecked(next);
  }, [assigned.data]);

  const toggleMenu = (menu: MenuItem, value: boolean) => {
    setChecked((previous) => {
      const next = { ...previous, [menu.id]: value };
      if (!value) {
        for (const child of menu.subMenu ?? []) {
          next[child.id] = false;
        }
      }
      return next;
    });
  };

  const save = async () => {
    if (!roleId || submitting || loading || error) return;
    for (const menu of menus) {
      if (
        checked[menu.id] &&
        menu.subMenu?.length &&
        !menu.subMenu.some((child) => checked[child.id])
      ) {
        showError(`Please select at least one submenu for ${menu.name}.`);
        return;
      }
    }
    const menuList = menus.flatMap((menu) => [
      { id: menu.id, operation: checked[menu.id] ? "assign" : "remove" },
      ...(menu.subMenu ?? []).map((child) => ({
        id: child.id,
        operation: checked[menu.id] && checked[child.id] ? "assign" : "remove",
      })),
    ]);
    setSubmitting(true);
    try {
      await postApi(MENU_ASSIGNMENT, accessToken(), { roleId, menuList });
      showSuccess("Menu assignments updated successfully.");
      setRefreshKey((key) => key + 1);
    } catch (cause) {
      showError(cause instanceof ApiError ? cause.message : GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className={styles.field}>
        <Select
          id="menu-role"
          label="Role"
          placeholder="Select Role"
          searchable
          options={(roles.data?.data ?? []).map((role) => ({
            value: String(role.roleId),
            label: roleLabel(role),
          }))}
          value={roleId}
          onChange={(value) => {
            setChecked({});
            setRoleId(value);
          }}
          disabled={roles.loading || submitting}
        />
      </div>
      {error ? null : !roleId ? (
        <EmptyStateMessage message="Select a role to manage menu access." />
      ) : loading ? (
        <Spinner />
      ) : menus.length === 0 ? (
        <EmptyStateMessage message="No menus available for this role." />
      ) : (
        <>
          <ul className={styles.menuList} aria-label="Menu permissions">
            {menus.map((menu) => (
              <li key={menu.id}>
                <Checkbox
                  id={`menu-${menu.id}`}
                  label={menu.name}
                  checked={Boolean(checked[menu.id])}
                  onChange={(value) => toggleMenu(menu, value)}
                  disabled={submitting}
                />
                {Boolean(menu.subMenu?.length) && (
                  <ul className={styles.subMenus} aria-label={`${menu.name} submenus`}>
                    {menu.subMenu?.map((child) => (
                      <li key={child.id}>
                        <Checkbox
                          id={`submenu-${child.id}`}
                          label={child.name}
                          checked={Boolean(checked[child.id])}
                          disabled={submitting || !checked[menu.id]}
                          onChange={(value) =>
                            setChecked((previous) => ({
                              ...previous,
                              [child.id]: value,
                            }))
                          }
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          <div className={styles.actions}>
            <Button
              variant="accent"
              disabled={submitting}
              onClick={() => void save()}
            >
              {submitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
